import { apiRequest } from "./client";
import type {
  DataSource,
  Dataset,
  DatasetField,
  DatasetMeasure,
  SchemaField,
} from "../types";

export const dataSourceApi = {
  list: () => apiRequest<DataSource[]>("/api/v1/admin/data-sources"),
  create: (payload: { name: string; type: string; description?: string; config: Record<string, unknown> }) =>
    apiRequest<DataSource>("/api/v1/admin/data-sources", { method: "POST", body: payload }),
  update: (id: string, payload: { name?: string; description?: string; config?: Record<string, unknown> }) =>
    apiRequest<DataSource>(`/api/v1/admin/data-sources/${id}`, { method: "PUT", body: payload }),
  setCredentials: (id: string, payload: Record<string, unknown>) =>
    apiRequest<void>(`/api/v1/admin/data-sources/${id}/credentials`, { method: "POST", body: { payload } }),
  test: (id: string) =>
    apiRequest<{ status: string; message?: string }>(`/api/v1/admin/data-sources/${id}/test`, { method: "POST" }),
  discover: (id: string) =>
    apiRequest<SchemaField[]>(`/api/v1/admin/data-sources/${id}/discover`, { method: "POST" }),
};

export interface SharePointSite {
  id: string;
  name: string;
  webUrl: string;
}

export interface SharePointList {
  id: string;
  name: string;
  webUrl: string;
  isDocumentLibrary: boolean;
}

export interface SharePointDrive {
  id: string;
  name: string;
  webUrl: string;
}

export interface SharePointDriveItem {
  id: string;
  name: string;
  isFolder: boolean;
  isExcel: boolean;
  size?: number;
}

export const sharePointApi = {
  searchSites: (q: string, tenantId: string) =>
    apiRequest<SharePointSite[]>(
      `/api/v1/admin/sharepoint/sites?q=${encodeURIComponent(q || "*")}&tenant_id=${encodeURIComponent(tenantId)}`,
    ),
  listLists: (siteId: string, tenantId: string) =>
    apiRequest<SharePointList[]>(
      `/api/v1/admin/sharepoint/sites/${encodeURIComponent(siteId)}/lists?tenant_id=${encodeURIComponent(tenantId)}`,
    ),
  listDrives: (siteId: string, tenantId: string) =>
    apiRequest<SharePointDrive[]>(
      `/api/v1/admin/sharepoint/sites/${encodeURIComponent(siteId)}/drives?tenant_id=${encodeURIComponent(tenantId)}`,
    ),
  browseDrive: (driveId: string, path: string, tenantId: string) =>
    apiRequest<SharePointDriveItem[]>(
      `/api/v1/admin/sharepoint/drives/${encodeURIComponent(driveId)}/items?path=${encodeURIComponent(path)}&tenant_id=${encodeURIComponent(tenantId)}`,
    ),
};

export interface RefreshRun {
  id: string;
  dataset_id: string;
  dataset_name: string;
  started_at: string;
  completed_at: string | null;
  status: "success" | "failed" | "running";
  row_count: number | null;
  error_message: string | null;
}

export const datasetApi = {
  list: () => apiRequest<Dataset[]>("/api/v1/admin/datasets"),
  listRecentRefreshRuns: () => apiRequest<RefreshRun[]>("/api/v1/admin/datasets/refresh-runs"),
  listMine: () => apiRequest<Dataset[]>("/api/v1/owner/datasets"),
  create: (payload: {
    name: string;
    description?: string;
    data_source_id: string;
    source_object: string;
    cache_ttl_seconds?: number;
  }) => apiRequest<Dataset>("/api/v1/admin/datasets", { method: "POST", body: payload }),
  listFields: (id: string) => apiRequest<DatasetField[]>(`/api/v1/admin/datasets/${id}/fields`),
  replaceFields: (id: string, fields: Omit<DatasetField, "id">[]) =>
    apiRequest<DatasetField[]>(`/api/v1/admin/datasets/${id}/fields`, { method: "PUT", body: { fields } }),
  listMeasures: (id: string) => apiRequest<DatasetMeasure[]>(`/api/v1/admin/datasets/${id}/measures`),
  createMeasure: (
    id: string,
    payload: { name: string; display_name: string; source_field: string; aggregation: string; format_string?: string },
  ) => apiRequest<DatasetMeasure>(`/api/v1/admin/datasets/${id}/measures`, { method: "POST", body: payload }),
  preview: (
    id: string,
    payload: { dimensions: string[]; measures: string[]; filters?: unknown[]; sort?: unknown[]; limit?: number },
  ) => apiRequest<Record<string, unknown>>(`/api/v1/admin/datasets/${id}/preview`, { method: "POST", body: payload }),
  refresh: (id: string) =>
    apiRequest<{ status: string; cache_keys_cleared: number; error?: string }>(
      `/api/v1/admin/datasets/${id}/refresh`,
      { method: "POST" },
    ),
};
